import React from "react";

import cx from "classnames";
import { find, noop, omit } from "lodash";
import scrollIntoView from "scroll-into-view-if-needed";
import { Ref } from "semantic-ui-react";

import type { Omit } from "~/types";

// Javascript's % operator is a remainder that keeps negative numbers negative.
// This wraps negative numbers back to the positive side of the number line.
// -13 % 64 === -13
// mod(-13, 64) == 51
const mod = (n: number, m: number): number => ((n % m) + m) % m;

const preventDefault = (event: Event | React.SyntheticEvent): boolean => {
	event.preventDefault();
	event.stopPropagation();
	return false;
};

const NavigationContext = React.createContext<KeyboardNavigation | null>(null);
NavigationContext.displayName = "NavigationContext";
const focusableClassName = "keyboard-navigation-focusable";

interface FocusableProps {
	onFocus: (event: Event | React.SyntheticEvent | null) => void;
	onBlur: (event: Event | React.SyntheticEvent | null) => void;
	onEnterDown: (event: Event | React.SyntheticEvent | null) => void;
	className?: string;
	as?: React.ComponentType;
}

export class Focusable extends React.Component<FocusableProps> {
	static contextType = NavigationContext;
	declare context: React.ContextType<typeof NavigationContext>;

	static defaultProps = {
		onFocus: noop,
		onBlur: noop,
		onEnterDown: noop,
	};

	ref = React.createRef<HTMLElement>();

	componentDidMount() {
		if (!this.context) {
			throw new Error(
				`<Focusable> component created outside of a navigation context, please make sure it is the child of a
					KeyboardNavigation somewhere in the tree.`,
			);
		}

		this.context.childMounted(this);
	}

	componentWillUnmount() {
		this.context?.childUnmounted(this);
	}

	handleMouseEnter = (event: React.MouseEvent) => {
		this.context?.forceChildFocus(this, event);
	};

	handleMouseLeave = (event: React.MouseEvent) => {
		this.context?.handleChildUnfocus(this, event);
	};

	scrollIntoView(): void {
		if (this.ref.current) {
			scrollIntoView(this.ref.current, {
				scrollMode: "if-needed",
				block: "nearest",
				inline: "nearest",
			});
		}
	}

	handleInnerRef = (node: HTMLElement): void => {
		// @ts-ignore
		this.ref.current = node;
	};

	render(): React.ReactNode {
		const childProps: Omit<FocusableProps, "onFocus" | "onBlur" | "onEnterDown" | "as"> = omit(this.props, [
			"onFocus",
			"onBlur",
			"onEnterDown",
			"as",
		]);
		const ChildElement = this.props.as || "div";

		// We use a Ref here instead of a normal ref={this.ref} prop because Ref guarantees returning a DOM node,
		// whereas whatever might be passed in as the `as` prop doesn't always properly forward refs,
		// which means sometimes the ref is a React.Component instead of a DOM node.
		// We really need a real node here for checking against other nodes from querySelectorAll.
		return (
			<Ref innerRef={this.handleInnerRef}>
				<ChildElement
					{...childProps}
					className={cx(childProps.className, focusableClassName)}
					onMouseEnter={this.handleMouseEnter}
					onMouseLeave={this.handleMouseLeave}
				/>
			</Ref>
		);
	}
}

export class FocusableTracker {
	focusedIndex: number | null = null;
	focusables: Focusable[] = [];
	ref = React.createRef<HTMLElement>();

	constructor(ref: React.RefObject<HTMLElement>) {
		this.ref = ref;
		this.resetIndex();
	}

	resetIndex(): void {
		this.focusedIndex = null;
	}

	add(focusable: Focusable): void {
		this.focusables.push(focusable);
	}

	remove(focusable: Focusable): void {
		this.focusables.splice(this.focusables.indexOf(focusable), 1);
	}

	setFocus(value: number): void {
		this.focusedIndex = value;
	}

	adjustFocus(delta: number): void {
		// We start at null to mean "offscreen". When you add one (hit down) the first time, we should go to 0,
		// but if you hit up for the first navigation, we should wrap around to the bottom,
		// which is -1. Seed the focusedIndex to the right thing relative to the delta.
		if (this.focusedIndex === null) {
			if (delta > 0) {
				this.focusedIndex = -1;
			} else {
				this.focusedIndex = 0;
			}
		}

		this.focusedIndex = mod(this.focusedIndex + delta, this.focusables.length);
	}

	deltaTo(focusable: Focusable): number {
		const newIndex = this.indexOf(focusable);

		if (this.focusedIndex === null) {
			return newIndex + 1;
		}
		return newIndex - this.focusedIndex;
	}

	currentFocusable(): Focusable | undefined {
		if (this.ref.current && this.focusedIndex !== null) {
			const domNode = this.ref.current.querySelectorAll(`.${focusableClassName}`)[this.focusedIndex];
			return find(this.focusables, component => component.ref.current === domNode);
		}
		return undefined;
	}

	indexOf(focusable: Focusable): number {
		if (!this.ref.current || !focusable.ref.current) return -1;
		return Array.from(this.ref.current.querySelectorAll(`.${focusableClassName}`)).indexOf(focusable.ref.current);
	}
}

// Keyboard navigation group class that wraps all the Focusables that might get focused. Uses the Tracker to know what
// the currently focused index is and keeps that state there, and uses the DOM to know the focusable order by means
// of querySelectorAll.
interface KeyboardNavigationProps {
	onFocus?: (event: Event | React.SyntheticEvent | null, node: Focusable) => void;
	onBlur?: (event: Event | React.SyntheticEvent | null, node: Focusable) => void;
	onEnterDown?: (event: Event | React.SyntheticEvent | null, node: Focusable) => void;
}

class KeyboardNavigation extends React.Component<KeyboardNavigationProps> {
	static Focusable = Focusable;

	ref = React.createRef<HTMLDivElement>();
	tracker = new FocusableTracker(this.ref);

	componentDidMount() {
		document.addEventListener("keydown", this.onKeyDown.bind(this));
	}

	componentWillUnmount() {
		document.removeEventListener("keydown", this.onKeyDown.bind(this));
	}

	onKeyDown = (event: KeyboardEvent): void => {
		// Don't screw with the user's shortcut keys
		if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
			return;
		}

		// If it's an enter keycode, and there's something selected, fire the handler and consume the event
		if (event.keyCode === 13) {
			const currentFocusable = this.tracker.currentFocusable();
			if (currentFocusable) {
				this.firePropHandler(currentFocusable, "onEnterDown", event);
				preventDefault(event);
			}
		} else if (event.keyCode == 38) {
			this.moveFocus(-1, event);
		} else if (event.keyCode == 40) {
			this.moveFocus(1, event);
		}
	};

	// Actually moves focus to a new Focusable.
	moveFocus = (delta: number, event: Event | React.SyntheticEvent | null) => {
		const previousFocusable = this.tracker.currentFocusable();
		this.tracker.adjustFocus(delta);

		const nextFocusable = this.tracker.currentFocusable();
		if (previousFocusable) this.firePropHandler(previousFocusable, "onBlur", event);

		if (nextFocusable) {
			this.firePropHandler(nextFocusable, "onFocus", event);
			nextFocusable.scrollIntoView();

			if (event) preventDefault(event);
		} else {
			this.tracker.setFocus(-1);
		}
	};

	removeFocus() {
		const previousFocusable = this.tracker.currentFocusable();
		if (previousFocusable) this.firePropHandler(previousFocusable, "onBlur", null);
		this.tracker.resetIndex();
	}

	selectElement(index: number) {
		this.removeFocus();

		this.tracker.setFocus(index);
		const nextFocusable = this.tracker.currentFocusable();
		if (nextFocusable) {
			this.firePropHandler(nextFocusable, "onFocus", null);
		}
	}

	firePropHandler = (component: Focusable, propName: string, event: Event | React.SyntheticEvent | null) => {
		switch (propName) {
			case "onEnterDown":
				if (component.props.onEnterDown) component.props.onEnterDown(event);
				if (this.props.onEnterDown) this.props.onEnterDown(event, component);
				break;
			case "onBlur":
				if (component.props.onBlur) component.props.onBlur(event);
				if (this.props.onBlur) this.props.onBlur(event, component);
				break;
			case "onFocus":
				if (component.props.onFocus) component.props.onFocus(event);
				if (this.props.onFocus) this.props.onFocus(event, component);
				break;
			default:
				return;
		}
	};

	childMounted = (component: Focusable) => {
		this.tracker.add(component);
	};

	childUnmounted = (component: Focusable) => {
		this.tracker.remove(component);
	};

	// Happens when the component is mouseentered
	forceChildFocus = (component: Focusable, event: React.MouseEvent) => {
		this.moveFocus(this.tracker.deltaTo(component), event);
	};

	handleChildUnfocus = (component: Focusable, event: React.MouseEvent) => {
		this.firePropHandler(component, "onBlur", event);
		this.tracker.resetIndex();
	};

	render(): React.ReactNode {
		return (
			<NavigationContext.Provider value={this}>
				<div ref={this.ref}>{this.props.children}</div>
			</NavigationContext.Provider>
		);
	}
}

// WORKAROUND: STORYBOOK (https://github.com/styleguidist/react-docgen-typescript/issues/394)
export default KeyboardNavigation;
